import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  CircularProgress, 
  Paper 
} from '@mui/material';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { formatCurrency, handleError, formatDateLong } from '../../utils/lib';
import { useSnackbar } from '../../providers/SnackBarProvider';
import { useAppBar } from '../../providers/AppBarProvider';
import DailyCashDetailsList from '../../layouts/DailyCashDetailsList';
import { useTranslation } from 'react-i18next';

const ViewDailyCashDetails = () => {
  const { t } = useTranslation();
  const { date } = useParams();
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState([]);
  const [totalSells, setTotalSells] = useState(0);
  const [totalManualCashIn, setTotalManualCashIn] = useState(0);
  const [totalManualCashOut, setTotalManualCashOut] = useState(0);
  const [totalSpendings, setTotalSpendings] = useState(0);
  const [totalPurchases, setTotalPurchases] = useState(0);
  const snackbar = useSnackbar();
  const { updateTitle, toggleFab } = useAppBar();

  useEffect(() => {
    toggleFab(false);
    updateTitle('', t('view.dailyCashTitle', { date: formatDateLong(date) }));
    fetchDailyCashDetails();
  }, [date, t]);

  const fetchDailyCashDetails = async () => {
    setLoading(true);
    try {
      const params = {
        daily_cash_details: '',
        date: date
      };
      
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}finance`, 
        { params, withCredentials: true }
      );
      
      if (response.data && response.data.data) {
        const data = response.data.data;
        setDetails(data.details || []);
        setTotalSells(data.total_sells || 0);
        setTotalManualCashIn(data.total_manual_cash_in || 0);
        setTotalManualCashOut(data.total_manual_cash_out || 0);
        setTotalSpendings(data.total_spendings || 0);
        setTotalPurchases(data.total_purchases || 0);
      }
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <CircularProgress
      size={42}
      sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-21px', marginLeft: '-21px' }}
    />
  );

  return (
    <Container maxWidth="xl">
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%'
      }}>
        <Paper 
          elevation={3} 
          sx={{ 
            mb: 2, 
            overflow: 'auto', 
            borderRadius: 2 
          }}
        >
          <DailyCashDetailsList items={details} />
        </Paper>

        <Paper 
          elevation={3} 
          sx={{ 
            p: 2, 
            borderRadius: 2 
          }}
        >
          <Typography variant="body1" gutterBottom>
            {t('view.dailyCashIncome')} {formatCurrency(totalSells)}
          </Typography>
          <Typography variant="body1" gutterBottom>
            {t('view.dailyCashManualIncome')} {formatCurrency(totalManualCashIn)}
          </Typography>
          <Typography variant="body1" gutterBottom>
            {t('view.dailyCashSpendings')} {formatCurrency(totalSpendings)}
          </Typography>
          <Typography variant="body1" gutterBottom>
            {t('view.dailyCashManualOut')} {formatCurrency(totalManualCashOut)}
          </Typography>
          <Typography variant="body1" gutterBottom>
            {t('view.dailyCashPurchases')} {formatCurrency(totalPurchases)}
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default ViewDailyCashDetails; 